import { type ReactNode } from "react";
import { Link } from "react-router";

import EffectIcon from "@vega-assets/images/effect.svg?react";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import type { NavigationItem } from "./Sidebar.types";

type NavigationButtonProps = {
  item: NavigationItem;
  isCollapsed: boolean;
  isActive: boolean;
  isMobile?: boolean;
  onClick?: () => void;
  onNavigate?: (analyticsSelector?: string) => void;
  rightIcon?: ReactNode;
};

const STYLE_CONSTANTS = {
  iconBase: "h-6 w-6",
  buttonBase: "flex items-center",
  textBase: "text-text/sidebar/primary",
};

export const NavigationButton = ({
  item,
  isCollapsed,
  isActive,
  isMobile = false,
  onClick,
  onNavigate,
  rightIcon,
}: NavigationButtonProps) => {
  const link = (
    <Link
      className={cnTwMerge(
        STYLE_CONSTANTS.buttonBase,
        "rounded-companyBorderRadius py-3 text-sm font-semibold relative cursor-pointer",
        {
          "bg-background/component": isActive,
          [STYLE_CONSTANTS.textBase]: true,
          "hover:bg-background/component ": !isActive,
        },
        isCollapsed ? "justify-center px-2" : "px-3",
      )}
      data-testid={`sidebar-nav-${item.name}`}
      onClick={() => {
        if (isMobile) {
          onClick?.();
        }
        onNavigate?.(item.analyticsSelector);
      }}
      to={item.href}
      {...(item.analyticsSelector && {
        "data-analytics": item.analyticsSelector,
      })}
    >
      {isActive ? (
        <EffectIcon className="absolute -left-[1px] rounded-lg" width="67" />
      ) : null}
      {isTruthy(item.icon) && (
        <item.icon
          aria-hidden="true"
          className={cnTwMerge(STYLE_CONSTANTS.iconBase, "flex-shrink-0", {
            "mr-3": !isCollapsed,
            "fill-text/sidebar/primary text-text/sidebar/primary": isActive,
            "text-text/sidebar/secondary fill-text/sidebar/secondary group-hover:fill-text/sidebar/secondary opacity-70 defaultTransition":
              !isActive,
          })}
        />
      )}
      {!isCollapsed && (
        <span
          className={cnTwMerge(
            "hidden md:block flex-1 defaultTransition",
            STYLE_CONSTANTS.textBase,
            isActive ? "body-base-semibold" : "body-base-medium",
            isMobile ? "block" : "",
          )}
        >
          {item.name}
        </span>
      )}
      {rightIcon ? rightIcon : null}
    </Link>
  );

  if (!isCollapsed) {
    return link;
  }

  return (
    <Tooltip content={item.name} contentProps={{ side: "right" }}>
      {link}
    </Tooltip>
  );
};
